import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Home, CalendarDays, Camera, LogOut, Bell, FileText, ClipboardCheck } from "lucide-react";
import { api } from "../../lib/api";
import { LANG_OPTIONS, makeT } from "../../lib/staffI18n";

const StaffCtx = createContext(null);
export const useStaff = () => useContext(StaffCtx);

export default function StaffLayout() {
  const [staff, setStaff] = useState(undefined);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/staff/me")
      .then((r) => setStaff(r.data))
      .catch(() => setStaff(null));
    api
      .get("/staff/notifications")
      .then((r) => setUnread(r.data.filter((n) => !n.read).length))
      .catch(() => {});
  }, []);

  if (staff === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
      </div>
    );
  }
  if (staff === null) return <Navigate to="/staff/login" replace />;

  const lang = staff.language || "ja";
  const t = makeT(lang);

  const changeLang = async (code) => {
    try {
      await api.put("/staff/language", { language: code });
      setStaff((s) => ({ ...s, language: code }));
    } catch {}
  };

  const logout = async () => {
    await api.post("/staff/logout").catch(() => {});
    navigate("/staff/login");
  };

  const navItem = ({ isActive }) =>
    `flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] transition-colors ${isActive ? "text-accent" : "text-gray-500"}`;

  return (
    <StaffCtx.Provider value={{ staff, setStaff, t, lang, unread, setUnread }}>
      <div data-testid="staff-layout" className="flex min-h-screen flex-col bg-ink">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-coal/95 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-2">
            <img src="/neo-logo.png" alt="NEO" className="h-8 w-auto rounded" />
            <span className="text-xs font-bold text-white">{staff.display_name || staff.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="staff-language-select" value={lang} onChange={(e) => changeLang(e.target.value)} className="border border-line bg-ink px-2 py-1.5 text-xs text-gray-300 outline-none focus:border-accent">
              {LANG_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <button data-testid="staff-logout-button" onClick={logout} className="p-2 text-gray-400 transition-colors hover:text-accent" aria-label={t("logout")}>
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-20">
          <Outlet />
        </main>

        <nav className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-line bg-coal/95 backdrop-blur">
          <NavLink to="/staff" end data-testid="staff-nav-home" className={navItem}>
            <Home className="h-5 w-5" />
            {t("home")}
          </NavLink>
          <NavLink to="/staff/schedule" data-testid="staff-nav-schedule" className={navItem}>
            <CalendarDays className="h-5 w-5" />
            {t("schedule")}
          </NavLink>
          <NavLink to="/staff/survey" data-testid="staff-nav-survey" className={navItem}>
            <ClipboardCheck className="h-5 w-5" />
            {t("nav_survey")}
          </NavLink>
          <NavLink to="/staff/photos" data-testid="staff-nav-photos" className={navItem}>
            <Camera className="h-5 w-5" />
            {t("photos")}
          </NavLink>
          <NavLink to="/staff/report" data-testid="staff-nav-report" className={navItem}>
            <FileText className="h-5 w-5" />
            {t("nav_report")}
          </NavLink>
          <NavLink to="/staff" end data-testid="staff-nav-bell" onClick={(e) => { e.preventDefault(); navigate("/staff#notifications"); }} className={navItem}>
            <span className="relative">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span data-testid="staff-unread-badge" className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-ink">{unread}</span>}
            </span>
            {t("notifications")}
          </NavLink>
        </nav>
      </div>
    </StaffCtx.Provider>
  );
}
